import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { snoozeTarget, todayISO, addDaysISO, nowHHMM, type SnoozeKind } from './reminderTime'

interface Props {
  tz: string
  /** 该 reminder 当前的 dueTime,作为自选时间默认值 */
  currentTime: string | null
  onPick: (dueDate: string, dueTime: string | null) => void
}

/** 三个快捷项:kind → i18n key。 */
const QUICK: ReadonlyArray<readonly [SnoozeKind, string]> = [
  ['1h', 'snooze1h'],
  ['evening', 'snoozeEvening'],
  ['tomorrow', 'snoozeTomorrow'],
]

const itemStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none',
  background: 'none', borderRadius: 7, fontSize: 12, fontWeight: 500,
  color: 'hsl(var(--popover-foreground))', cursor: 'pointer',
}

/**
 * Snooze 菜单内容:三个快捷项 + 自选日期/时间。
 * 只渲染内容,不管理开合——宿主(列表行的 Popover / 贴纸的手写弹层)自己负责显示与关闭。
 */
export function SnoozeMenu({ tz, currentTime, onPick }: Props) {
  const { t } = useTranslation('reminders')
  const today = todayISO(tz)
  const [date, setDate] = useState(() => addDaysISO(1, tz))
  const [time, setTime] = useState(() => currentTime ?? '09:00')

  // 目标已经过去(挪完立刻又 overdue)或日期为空 → 不给确认。
  // 注意「今天 + 清空时间」也算过去:isDueNow(reminderTime.ts) 对无 dueTime 的今天
  // 提醒直接判 due now,而 snoozeTo 会清掉 surfaced 台账,贴纸挪完立刻重弹,
  // 用户唯一出口只剩 Dismiss——所以今天这一天必须连空时间一起拦。
  const invalid = !date || date < today || (date === today && (!time || time <= nowHHMM(tz)))

  return (
    <div style={{ minWidth: 224 }}>
      {QUICK.map(([kind, key]) => (
        <button key={kind} style={itemStyle} onClick={() => {
          const { dueDate, dueTime } = snoozeTarget(kind, tz)
          onPick(dueDate, dueTime)
        }}>{t(key)}</button>
      ))}
      <div style={{ borderTop: '1px solid hsl(var(--border))', marginTop: 4, paddingTop: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '0 6px' }}>
          <input type="date" className="qa-date" value={date} min={today} aria-label={t('pickDate')}
            style={{ flex: 1, minWidth: 0 }}
            onChange={(e) => setDate(e.target.value)} />
          {/* 不锁宽:zh locale 原生 time input 带「上午/下午」前缀,74px 会截断 */}
          <input type="time" className="qa-time" value={time} aria-label={t('pickTime')}
            style={{ flexShrink: 0 }}
            onChange={(e) => setTime(e.target.value)} />
        </div>
        <div style={{ padding: '6px 6px 2px' }}>
          <button disabled={invalid} onClick={() => onPick(date, time || null)} style={{
            width: '100%', height: 26, borderRadius: 8, border: 'none',
            cursor: invalid ? 'default' : 'pointer',
            background: invalid ? 'hsl(var(--muted))' : 'hsl(var(--primary))',
            color: invalid ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary-foreground))',
            fontSize: 11.5, fontWeight: 700,
          }}>{t('snoozeMove')}</button>
        </div>
      </div>
    </div>
  )
}
